import { JI_RATIOS, SWARA_SYMBOLS, gradePitch, smoothPitchHz } from '@swarasense/shared';
import type { PitchBand, PracticeLevel, RecordedSample, SessionReport, SwaraSymbol } from '@swarasense/shared';
import { centsToY } from './ribbon-geometry.js';
import type { RibbonView } from './ribbon-geometry.js';
import { cssVar, THEME_CHANGE_EVENT } from './theme/theme.js';

/**
 * The static session-review contour + on-device playback mechanics, shared by
 * whatever renders the take's results (Task 3's `results-panel.ts`). Kept
 * free of the results LAYOUT (that's `results-panel.ts`'s job, matching the
 * single-page mockup) — this file only owns: drawing the whole take's pitch
 * trace onto a `<canvas>`, and driving a `<audio>` + sweeping cursor over it.
 */

/** Ladder band colours, reused here so the review contour matches the live ladder — read
 * from the theme's CSS tokens (never a literal hex) so it retints on a theme switch. */
function bandColors(): Record<PitchBand, string> {
  return {
    excellent: cssVar('--band-excellent'),
    good: cssVar('--band-good'),
    off: cssVar('--band-off'),
    wrong: cssVar('--band-wrong'),
  };
}

const PAD_Y = 18;

/** An on-device recording of the take (never uploaded). */
export interface SessionRecording {
  url: string;
  mimeType: string;
}

/** The static plot's time→x mapping, so the playback cursor lines up with the contour. */
export interface PlotMapping {
  left: number;
  right: number;
  t0: number;
  durMs: number;
}

/** The <audio> from the last render — paused before each re-render so it can't outlive its results. */
let activeAudio: HTMLAudioElement | null = null;

/** The playback cursor's `requestAnimationFrame` handle from the last `mountPlayback`
 * call — cancelled alongside `activeAudio` so the cursor loop can't keep drawing to a
 * detached canvas after the results are torn down (see `stopPlayback`). */
let activeRaf = 0;

/** The last `drawReview` call's inputs, so a theme toggle can redraw the static contour
 * with the new palette even though this canvas isn't on an animation loop like the ribbon. */
let lastDraw: Parameters<typeof drawReview> | null = null;
let redrawOnThemeChangeWired = false;
function wireRedrawOnThemeChange(): void {
  if (redrawOnThemeChangeWired) return;
  redrawOnThemeChangeWired = true;
  document.addEventListener(THEME_CHANGE_EVENT, () => {
    if (lastDraw) drawReview(...lastDraw);
  });
}

/**
 * Draws the whole take's pitch contour onto `canvas`: one horizontal rung per
 * swara (dimmed when outside the raga's allowed set), out-of-scale episodes
 * shaded, and the sung pitch as one continuous curve coloured per-frame by
 * its graded band (using the same `gradePitch`/`smoothPitchHz` the live
 * ribbon and `analyzeSession` use, so a gamaka wobble reads as its centre
 * swara rather than smearing across bands). Reuses Task 4's ribbon geometry
 * (`centsToY`) rather than a second, hand-rolled mapping. Returns the
 * resulting time→x mapping (for `mountPlayback`'s cursor), or `null` when
 * there are no samples to plot.
 *
 * Self-wires a redraw-on-theme-change listener (idempotent) and remembers
 * its own inputs, so any caller gets automatic retinting for free.
 */
export function drawReview(
  canvas: HTMLCanvasElement,
  samples: RecordedSample[],
  saHz: number,
  allowed: readonly SwaraSymbol[],
  targetCents: Record<string, number>,
  episodes: SessionReport['episodes'],
  level: PracticeLevel,
): PlotMapping | null {
  wireRedrawOnThemeChange();
  lastDraw = [canvas, samples, saHz, allowed, targetCents, episodes, level];

  const ctx = canvas.getContext('2d')!;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const W = canvas.clientWidth;
  const H = canvas.clientHeight;
  canvas.width = Math.max(1, Math.round(W * dpr));
  canvas.height = Math.max(1, Math.round(H * dpr));
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, W, H);

  const axisX = Math.min(48, W * 0.09);
  const left = axisX + 8;
  const right = W - 10;
  const view: RibbonView = { minCents: 0, maxCents: 1200, height: H, pad: PAD_Y };
  const yOf = (cents: number) => centsToY(cents, view);
  const set = new Set(allowed);

  // Rung/ink colours read live from the theme's CSS tokens — never a literal hex —
  // so a theme toggle retints this static plot the next time it's (re)drawn.
  const saLine = cssVar('--sa-line');
  // Fix (contrast blocker): the "Ṡ" rung label is drawn on the review canvas's
  // background, not on a solid accent fill — see ribbon.ts's identical fix.
  const saText = cssVar('--accent');
  const allowedLine = cssVar('--ink-faint');
  const allowedText = cssVar('--ink-strong');
  const dimLine = cssVar('--muted-ghost');
  const dimText = cssVar('--muted-faint');
  const driftFill = cssVar('--drift-highlight');
  const bands = bandColors();

  ctx.textBaseline = 'middle';
  const rung = (symbol: string, cents: number, isSa: boolean, allowedR: boolean) => {
    const y = yOf(cents);
    ctx.strokeStyle = isSa ? saLine : allowedR ? allowedLine : dimLine;
    ctx.lineWidth = isSa ? 1.5 : 1;
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(right, y);
    ctx.stroke();
    ctx.fillStyle = isSa ? saText : allowedR ? allowedText : dimText;
    ctx.font = `${isSa || allowedR ? 700 : 500} 12px system-ui, sans-serif`;
    ctx.textAlign = 'right';
    ctx.fillText(symbol, axisX, y);
  };
  for (const s of SWARA_SYMBOLS) rung(s, 1200 * Math.log2(JI_RATIOS[s]), s === 'S', set.has(s));
  rung('Ṡ', 1200, true, true);

  if (samples.length === 0) return null;
  const t0 = samples[0]!.t;
  const durMs = Math.max(1, samples[samples.length - 1]!.t - t0);
  const xOf = (tMs: number) => left + ((tMs - t0) / durMs) * (right - left);

  // Highlight the drifts.
  ctx.fillStyle = driftFill;
  for (const e of episodes) {
    const x1 = xOf(t0 + e.startSec * 1000);
    const x2 = xOf(t0 + e.endSec * 1000);
    ctx.fillRect(x1, PAD_Y, Math.max(2, x2 - x1), H - 2 * PAD_Y);
  }

  // Pitch contour across the whole session (break on silences > 250 ms).
  ctx.lineWidth = 1.8;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  let prev: { t: number; x: number; y: number } | null = null;
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i]!;
    // Draw the raw pitch (shows the gamaka wiggle) but COLOUR by the smoothed
    // pitch — so an oscillation around a swara reads as that swara, not red.
    // Banded (excellent/good/off/wrong) via the same grader driving the live ladder.
    const grade = gradePitch(smoothPitchHz(samples, i, saHz), saHz, targetCents, level);
    const total = 1200 * Math.log2(s.freqHz / saHz);
    const disp = ((total % 1200) + 1200) % 1200;
    const x = xOf(s.t);
    const y = yOf(disp);
    if (prev && s.t - prev.t <= 250) {
      ctx.strokeStyle = bands[grade.band];
      ctx.beginPath();
      ctx.moveTo(prev.x, prev.y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    prev = { t: s.t, x, y };
  }
  return { left, right, t0, durMs };
}

/**
 * Wires an on-device `<audio>` playback of the take to a play button and a
 * cursor overlay canvas that sweeps `mapping`'s time→x range in step with
 * playback — plus any `.seek` buttons already in `container` (seek-to-point
 * controls a caller may render alongside the review). Looks up
 * `#review-cursor`/`#play-btn` inside `container`; a no-op if either is
 * missing, so a caller with no playback UI (no recording captured) can skip
 * rendering them instead of guarding every call site here.
 */
export function mountPlayback(
  container: HTMLElement,
  mapping: PlotMapping,
  recording: SessionRecording,
): void {
  const cursor = container.querySelector<HTMLCanvasElement>('#review-cursor');
  const playBtn = container.querySelector<HTMLButtonElement>('#play-btn');
  if (!cursor || !playBtn) return;

  const audio = new Audio(recording.url);
  activeAudio = audio;

  const cctx = cursor.getContext('2d')!;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const W = cursor.clientWidth;
  const H = cursor.clientHeight;
  cursor.width = Math.max(1, Math.round(W * dpr));
  cursor.height = Math.max(1, Math.round(H * dpr));
  cctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const draw = (tSec: number) => {
    cctx.clearRect(0, 0, W, H);
    const frac = (tSec * 1000 - mapping.t0) / mapping.durMs;
    const x = Math.max(
      mapping.left,
      Math.min(mapping.right, mapping.left + frac * (mapping.right - mapping.left)),
    );
    cctx.strokeStyle = cssVar('--ink-strong');
    cctx.lineWidth = 2;
    cctx.beginPath();
    cctx.moveTo(x, PAD_Y);
    cctx.lineTo(x, H - PAD_Y);
    cctx.stroke();
  };

  const frame = () => {
    draw(audio.currentTime);
    if (!audio.paused && !audio.ended) activeRaf = requestAnimationFrame(frame);
  };

  playBtn.addEventListener('click', () => {
    if (audio.paused) void audio.play();
    else audio.pause();
  });
  audio.addEventListener('play', () => {
    playBtn.textContent = '⏸';
    playBtn.setAttribute('aria-label', 'Pause recording');
    cancelAnimationFrame(activeRaf);
    activeRaf = requestAnimationFrame(frame);
  });
  audio.addEventListener('pause', () => {
    playBtn.textContent = '▶';
    playBtn.setAttribute('aria-label', 'Play recording');
    cancelAnimationFrame(activeRaf);
  });
  audio.addEventListener('ended', () => {
    playBtn.textContent = '▶';
    playBtn.setAttribute('aria-label', 'Play recording');
    cancelAnimationFrame(activeRaf);
    draw(0);
  });

  for (const b of container.querySelectorAll<HTMLButtonElement>('.seek')) {
    b.addEventListener('click', () => {
      audio.currentTime = mapping.t0 / 1000 + Number(b.dataset.t);
      void audio.play();
    });
  }
}

/**
 * Pauses any in-flight take playback and cancels its cursor `requestAnimationFrame`
 * loop — called both at the top of `results-panel.ts`'s `renderResultsPanel` (so a
 * fresh review can't inherit a previous take's audio) and by its "New take" handler
 * (so clearing `#results` mid-playback can't leave the detached `<audio>` playing or
 * the rAF loop drawing to a now-unmounted canvas — `container.innerHTML = ''` alone
 * stops neither).
 */
export function stopPlayback(): void {
  if (activeAudio) {
    activeAudio.pause();
    activeAudio = null;
  }
  cancelAnimationFrame(activeRaf);
  activeRaf = 0;
}
